import React from 'react';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useTranslation } from 'react-i18next';
import { Polygon } from '../../../context/context';
import { logEvent } from '../../../services/analytics';

interface PolygonContextMenuProps {
    contextMenu: { x: number, y: number, polygon: Polygon } | null;
    onClose: () => void;
    onEditStyle: (polygon: Polygon) => void;
    onImportOSMLocations: (polygon: Polygon) => void;
    onGenerateMeetingPoints: (polygon: Polygon) => void;
    onGetPopulationData: (polygon: Polygon) => void;
    onRunSpatialAnalysis: (polygon: Polygon) => void;
    onCreateSpatialAnalysisTicket: (polygon: Polygon) => void;
    canImportOSMLocations?: boolean;
    canGenerateMeetingPoints?: boolean;
    canGetPopulationData?: boolean;
    canRunSpatialAnalysis?: boolean;
    isCreatingSpatialAnalysisTicket?: boolean;
}

export default function PolygonContextMenu({
    contextMenu,
    onClose,
    onEditStyle,
    onImportOSMLocations,
    onGenerateMeetingPoints,
    onGetPopulationData,
    onRunSpatialAnalysis,
    onCreateSpatialAnalysisTicket,
    canImportOSMLocations = true,
    canGenerateMeetingPoints = true,
    canGetPopulationData = true,
    canRunSpatialAnalysis = true,
    isCreatingSpatialAnalysisTicket = false,
}: PolygonContextMenuProps) {
    const { t } = useTranslation('map');
    if (!contextMenu) return null;

    const handleEditStyle = () => {
        logEvent({
            category: 'Transit Analysis',
            action: 'PolygonContextMenuEditStyle',
            label: contextMenu.polygon.name || 'Unnamed Polygon',
            polygon_id: contextMenu.polygon.id,
            polygon_vertex_count: contextMenu.polygon.coordinates[0]?.length || 0
        });
        onEditStyle(contextMenu.polygon);
        onClose();
    };

    const handleGetPopulationData = () => {
        logEvent({
            category: 'Transit Analysis',
            action: 'PolygonContextMenuGetPopulationData',
            label: contextMenu.polygon.name || 'Unnamed Polygon',
            polygon_id: contextMenu.polygon.id,
            polygon_vertex_count: contextMenu.polygon.coordinates[0]?.length || 0
        });
        onGetPopulationData(contextMenu.polygon);
        onClose();
    };

    const handleImportOSMLocations = () => {
        logEvent({
            category: 'Transit Analysis',
            action: 'PolygonContextMenuImportOSMLocations',
            label: contextMenu.polygon.name || 'Unnamed Polygon',
            polygon_id: contextMenu.polygon.id,
            polygon_vertex_count: contextMenu.polygon.coordinates[0]?.length || 0
        });
        onImportOSMLocations(contextMenu.polygon);
        onClose();
    };

    const handleGenerateMeetingPoints = () => {
        logEvent({
            category: 'Transit Analysis',
            action: 'PolygonContextMenuGenerateMeetingPoints',
            label: contextMenu.polygon.name || 'Unnamed Polygon',
            polygon_id: contextMenu.polygon.id,
            polygon_vertex_count: contextMenu.polygon.coordinates[0]?.length || 0
        });
        onGenerateMeetingPoints(contextMenu.polygon);
        onClose();
    };

    const handleRunSpatialAnalysis = () => {
        logEvent({
            category: 'Transit Analysis',
            action: 'PolygonContextMenuRunSpatialAnalysis',
            label: contextMenu.polygon.name || 'Unnamed Polygon',
            polygon_id: contextMenu.polygon.id,
            polygon_vertex_count: contextMenu.polygon.coordinates[0]?.length || 0
        });
        onRunSpatialAnalysis(contextMenu.polygon);
        onClose();
    };

    const handleCreateSpatialAnalysisTicket = () => {
        logEvent({
            category: 'Transit Analysis',
            action: 'PolygonContextMenuCreateSpatialAnalysisTicket',
            label: contextMenu.polygon.name || 'Unnamed Polygon',
            polygon_id: contextMenu.polygon.id,
            polygon_vertex_count: contextMenu.polygon.coordinates[0]?.length || 0
        });
        onCreateSpatialAnalysisTicket(contextMenu.polygon);
        onClose();
    };

    return (
        <Menu
            open={Boolean(contextMenu)}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={
                contextMenu
                    ? { top: contextMenu.y, left: contextMenu.x }
                    : undefined
            }
            PaperProps={{
                sx: {
                    boxShadow: 3,
                    minWidth: 120,
                }
            }}
        >
            <MenuItem onClick={handleEditStyle}>
                {t('contextMenu.editStyle')}
            </MenuItem>
            <MenuItem onClick={handleGetPopulationData} disabled={!canGetPopulationData}>
                {t('contextMenu.getPopulationData')}
            </MenuItem>
            <MenuItem onClick={handleImportOSMLocations} disabled={!canImportOSMLocations}>
                {t('contextMenu.getKeyLocations')}
            </MenuItem>
            <MenuItem onClick={handleGenerateMeetingPoints} disabled={!canGenerateMeetingPoints}>
                {t('contextMenu.generateMeetingPoints')}
            </MenuItem>
            {canRunSpatialAnalysis && (
                <MenuItem onClick={handleRunSpatialAnalysis}>
                    <ListItemIcon>
                        <TravelExploreIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('contextMenu.runSpatialAnalysis')}</ListItemText>
                </MenuItem>
            )}
            <MenuItem onClick={handleCreateSpatialAnalysisTicket} disabled={isCreatingSpatialAnalysisTicket}>
                {isCreatingSpatialAnalysisTicket
                    ? t('contextMenu.creatingSpatialAnalysisTicket')
                    : t('contextMenu.createSpatialAnalysisTicket')}
            </MenuItem>
        </Menu>
    );
} 
